import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import Logo from '../ui/Logo';
import { cmsAPI } from '../../services/api';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  // Fetch site settings for contact info
  const { data: settings } = useQuery('siteSettings', async () => {
    const response = await cmsAPI.getSettings();
    return response.data;
  }, { staleTime: 300000 });


  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Logo size="2xl" variant="light" />
            <p className="text-gray-400 text-sm leading-relaxed">
              Founded by Engineers. Inspired by Children. Building the Future Together.
            </p>
            <div className="flex space-x-4">
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-500 transition-colors"
                >
                  <Facebook size={18} />
                </a>
              )}
              {settings?.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-500 transition-colors"
                >
                  <Instagram size={18} />
                </a>
              )}
              {settings?.linkedin_url && (
                <a
                  href={settings.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-500 transition-colors"
                >
                  <Linkedin size={18} />
                </a>
              )}
              {settings?.youtube_url && (
                <a
                  href={settings.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-primary-500 transition-colors"
                >
                  <Youtube size={18} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors">
                  Programs
                </Link>
              </li>
              <li>
                <Link to="/registration" className="text-gray-400 hover:text-white transition-colors">
                  Registration
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-white transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Programs</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/programs" className="text-gray-400 hover:text-white transition-colors">
                  All Programs
                </Link>
              </li>
              <li>
                <Link to="/registration" className="text-gray-400 hover:text-white transition-colors">
                  View Sessions
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact - Dynamic from CMS */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin size={20} className="text-primary-400 flex-shrink-0 mt-1" />
                <span className="text-gray-400">{settings?.address || 'Clayton, NC'}</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={20} className="text-primary-400" />
                <a
                  href={`mailto:${settings?.contact_email || 'info@futuremindsacademy.net'}`}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {settings?.contact_email || 'info@futuremindsacademy.net'}
                </a>
              </li>
              {settings?.contact_phone && (
                <li className="flex items-center space-x-3">
                  <Phone size={20} className="text-primary-400" />
                  <a
                    href={`tel:${settings.contact_phone}`}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    {settings.contact_phone}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {currentYear} Future Minds Academy. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs text-center max-w-2xl">
              Future Minds Academy is a registered LLC at the State of North Carolina.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
